import {
    getEmployeeInfo,
    updateEmployeeIdentification,
} from '@/modules/Employee/services'
import {
    getCurrentEmployee,
    updateCurrentEmployee,
} from '@/modules/Account/components/services'
import { EmployeeConfimationValues } from '@/modules/Employee/types'
import { formatPGDate } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { GridHookForm, Loading } from 'vnetwork-ui'
import { FormValues } from '../../../Confirmation'
import { validateStep } from '../../../Profile/schema'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface IdentificationProps {
    employeeId?: number
    isSelf?: boolean
}

const Identification: React.FC<IdentificationProps> = ({
    employeeId,
    isSelf = false,
}) => {
    const params = useParams()
    const id = employeeId || Number(params.id)
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler({
        queryKey: isSelf
            ? [QUERY_KEYS.CURRENT_USER_PROFILE]
            : [QUERY_KEYS.EMPLOYEE, id],
        queryFn: () => (isSelf ? getCurrentEmployee() : getEmployeeInfo(id)),
        enabled: isSelf || !!id,
        loadingComponent: (
            <div className="flex justify-center items-center min-h-[200px]">
                <Loading.CircularProgress width={40} height={40} />
            </div>
        ),
    })

    const { mutateAsync: updateEmployeeMutation, isPending: isUpdatePending } =
        useMutationWithHandler(
            {
                mutationFn: (isSelf
                    ? updateCurrentEmployee
                    : updateEmployeeIdentification) as any,
            },
            {
                reactQueryKeys: isSelf
                    ? QUERY_KEYS.CURRENT_USER_PROFILE
                    : QUERY_KEYS.EMPLOYEES,
                successMessage: 'employee-update-success',
                errorMessage: 'error-occurred',
            }
        )

    const formControl = useForm<any>({
        defaultValues: {
            employee_idno: data?.result?.employee_idno || '',
            employee_idno_issued_on:
                data?.result?.employee_idno_issued_on || '',
            employee_idno_issued_by:
                data?.result?.employee_idno_issued_by ||
                'Cục Cảnh sát quản lý hành chính về trật tự xã hội',
        },
        resolver: yupResolver(
            validateStep({ activeTab: 'Identification' }) as any
        ),
    })

    useEffect(() => {
        formControl.reset({
            employee_idno: data?.result?.employee_idno || '',
            employee_idno_issued_on:
                data?.result?.employee_idno_issued_on || '',
            employee_idno_issued_by:
                data?.result?.employee_idno_issued_by ||
                'Cục Cảnh sát quản lý hành chính về trật tự xã hội',
        })
    }, [data])

    const onSubmit = async (values: Partial<EmployeeConfimationValues>) => {
        const payload = {
            ...values,
            employee_idno_issued_on: values?.employee_idno_issued_on
                ? formatPGDate(
                      values?.employee_idno_issued_on,
                      'YYYY-MM-DDTHH:mm:ss'
                  )
                : null,
        }

        if (isSelf) {
            await updateEmployeeMutation(payload as any)
        } else {
            await updateEmployeeMutation({
                id,
                employee: payload as Partial<FormValues>,
            } as any)
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-6">
            <GridHookForm
                formControl={formControl}
                onSubmit={onSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                data={[
                    {
                        formItemProps: {
                            type: 'textfield',
                            name: 'employee_idno',
                            placeholder: t('employees.idno'),
                            label: t('employees.idno') + ' *',
                        },
                    },
                    {
                        formItemProps: {
                            type: 'datepicker',
                            name: 'employee_idno_issued_on',
                            placeholder: t('employees.idno_issued_on'),
                            label: t('employees.idno_issued_on') + ' *',
                        },
                    },
                    {
                        formItemProps: {
                            type: 'textfield',
                            name: 'employee_idno_issued_by',
                            placeholder: t('employees.idno_issued_by'),
                            label: t('employees.idno_issued_by') + ' *',
                        },
                        className: 'md:col-span-2',
                    },
                ]}
                actions={[
                    {
                        title: t('save-changes'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isUpdatePending,
                        disabled:
                            isUpdatePending || !formControl.formState.isDirty,
                    },
                ]}
            />
        </div>
    )
}

export default Identification
